import { Box, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { kioskClockInRequest } from 'api/requests/kiosk-session-requests';
import UPButton from 'components/button';
import UPDatepicker from 'components/datepicker';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import UPInput from 'components/input';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import kioskSessionService from 'services/kiosk-session-service';
import { useAppSelector } from 'store';
import { kioskSessionSelector } from 'store/selectors/kiosk-session-selectors';
import {
  GENERAL__ERROR_MESSAGE,
  GENERAL__SELECT,
  KIOSK__COST_CENTRE_LABEL,
  KIOSK__START_REGISTRATION_BEFORE_PLANNED_START,
  KIOSK__START_REGISTRATION_BUTTON,
  KIOSK__START_REGISTRATION_DATE_LABEL,
  KIOSK__START_REGISTRATION_END_LABEL,
  KIOSK__START_REGISTRATION_NO_ELIGIBLE_PLANNING,
  KIOSK__START_REGISTRATION_PLANNING_LABEL,
  KIOSK__START_REGISTRATION_POLICIES_INTRO,
  KIOSK__START_REGISTRATION_POLICIES_TITLE,
  KIOSK__START_REGISTRATION_START_LABEL,
  KIOSK__START_REGISTRATION_TITLE,
} from 'translations/constants';
import { TKioskClockIn } from 'types/kiosk/TKioskClockIn';
import { currentTimeHHmm, isoToYmd, kioskDateBounds, localDateToYmd, ymdToLocalDate } from 'utils/kiosk-date';
import { mapKioskError } from 'utils/kiosk-error';
import { formatKioskTimeInput } from './kiosk-time-input';
import { useEndKioskSession } from './useEndKioskSession';
import { useKioskStartRegistrationData } from './useKioskStartRegistrationData';
import {
  KioskErrorMessage,
  KioskFieldContainer,
  KioskFieldRow,
  KioskNoPlanningMessage,
  KioskPoliciesAccordion,
  KioskPoliciesDetails,
  KioskPoliciesIntro,
  KioskPoliciesSummary,
  KioskPolicyText,
  KioskStageActions,
  KioskStageFormColumn,
  KioskStageSubtitle,
  KioskStageTitle,
} from './styles';

interface KioskStartRegistrationViewProps {
  employeeId: number;
  employeeName: string;
  /** Set when tapped from the Planned section — locks the planning picker to this planning. */
  lockedContractDateId: number | null;
  pinVerificationToken: string | null;
  onSuccess: (employeeName: string) => void;
  onEmployeeUnavailable: () => void;
  onPinVerificationExpired: () => void;
  /** Reports submit-in-flight state so the persistent header can disable its close button. */
  onBusyChange: (busy: boolean) => void;
}

interface IStartRegistrationForm {
  contractDateId: string; // '' = no planning (cost-centre path)
  date: Date;
  startWork: string; // "HH:mm"
  costCentreId: string;
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const KioskStartRegistrationView = ({
  employeeId,
  employeeName,
  lockedContractDateId,
  pinVerificationToken,
  onSuccess,
  onEmployeeUnavailable,
  onPinVerificationExpired,
  onBusyChange,
}: KioskStartRegistrationViewProps) => {
  const [t] = useTranslation();
  const session = useAppSelector(kioskSessionSelector);
  const endSession = useEndKioskSession();
  const hasBoundCostCentre = session?.costCentreId != null;

  const { data, status, policies } = useKioskStartRegistrationData(employeeId);

  const [polOpen, setPolOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    onBusyChange(submitting);
  }, [submitting, onBusyChange]);

  const { control, handleSubmit, reset, setValue, watch } = useForm<IStartRegistrationForm>({
    defaultValues: { contractDateId: '', date: new Date(), startWork: '', costCentreId: '' },
  });

  const selectedContractDateId = watch('contractDateId');
  const startWork = watch('startWork');

  const { minDate, maxDate } = useMemo(() => kioskDateBounds(), []);

  // A fetch that resolves to 'unavailable' (employee left the location) closes the view.
  useEffect(() => {
    if (status === 'unavailable') onEmployeeUnavailable();
  }, [status, onEmployeeUnavailable]);

  // Prefill the form once data arrives (locked planning wins, else the backend's selected* prefills).
  useEffect(() => {
    if (!data) return;
    setSubmitError(null);
    const locked = lockedContractDateId
      ? data.planningDropdownItems.find(p => p.contractDateId === lockedContractDateId)
      : undefined;
    // From the "all" section (no locked planning) auto-select the first eligible planning so the
    // picker is never left unselected.
    const prefillPlanning = lockedContractDateId ? locked : data.planningDropdownItems[0];
    const lockedDate = locked?.date ?? data.selectedDate;

    let costCentreId = '';
    if (prefillPlanning?.costCenterId != null) {
      costCentreId = String(prefillPlanning.costCenterId);
    } else if (data.selectedCostCentreId != null) {
      costCentreId = String(data.selectedCostCentreId);
    }

    reset({
      contractDateId: prefillPlanning ? String(prefillPlanning.contractDateId) : '',
      // Planned card keeps its planned date; the "all" section uses "now" (a cross-day planned
      // start can fall on a prior day).
      date: lockedContractDateId && lockedDate ? ymdToLocalDate(isoToYmd(lockedDate)) : new Date(),
      // Clock-in always starts at the actual tap time, not the planned start.
      startWork: currentTimeHHmm(),
      costCentreId,
    });
  }, [data, lockedContractDateId, reset]);

  const planningItems: UPDropdownItem[] = useMemo(
    () => (data?.planningDropdownItems ?? []).map(p => ({ value: String(p.contractDateId), label: p.label })),
    [data],
  );
  const costCentreItems: UPDropdownItem[] = useMemo(
    () => (data?.costCentres ?? []).map(c => ({ value: String(c.value), label: c.label })),
    [data],
  );

  const selectedPlanning = useMemo(
    () => data?.planningDropdownItems.find(p => String(p.contractDateId) === selectedContractDateId),
    [data, selectedContractDateId],
  );
  const endWorkLabel = selectedPlanning?.endWork ?? data?.selectedEndWork ?? '';

  // Picking a planning keeps the start at "now"; only the cost centre follows the selection.
  const handlePlanningChange = (value: string) => {
    setValue('contractDateId', value);
    const planning = data?.planningDropdownItems.find(p => String(p.contractDateId) === value);
    if (planning) {
      // Start stays "now" (actual tap time); only the cost centre follows the selected planning.
      setValue('date', new Date());
      setValue('startWork', currentTimeHHmm());
      setValue('costCentreId', String(planning.costCenterId));
    }
  };

  const startBeforePlannedStart =
    data?.allowRegistrationBeforePlannedStart === false &&
    Boolean(selectedPlanning) &&
    TIME_RE.test(startWork) &&
    startWork < (selectedPlanning?.startWork ?? '');

  const canSubmit = !submitting && status === 'ready' && TIME_RE.test(startWork) && !startBeforePlannedStart;

  const onSubmit = async (form: IStartRegistrationForm) => {
    const token = kioskSessionService.getToken();
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body: TKioskClockIn = {
        employeeId,
        date: localDateToYmd(form.date),
        startWork: form.startWork,
        latitude: null,
        longitude: null,
        ...(form.contractDateId ? { contractDateId: Number(form.contractDateId) } : {}),
        ...(hasBoundCostCentre || !form.costCentreId ? {} : { costCentreId: Number(form.costCentreId) }),
        ...(pinVerificationToken ? { pinVerificationToken } : {}),
      };
      await kioskClockInRequest(token, body);
      onSuccess(employeeName);
    } catch (e) {
      const httpStatus = (e as { response?: { status?: number } })?.response?.status;
      const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? '';
      if (httpStatus === 401) {
        if (code.includes('PIN_VERIFICATION_REQUIRED')) {
          onPinVerificationExpired();
          return;
        }
        endSession();
        return;
      }
      if (code.includes('EMPLOYEE_NOT_AT_LOCATION')) {
        onEmployeeUnavailable();
        return;
      }
      setSubmitError(mapKioskError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const showCostCentre = !hasBoundCostCentre && costCentreItems.length > 0;

  return (
    <>
      <KioskStageTitle>{t(KIOSK__START_REGISTRATION_TITLE)}</KioskStageTitle>
      <KioskStageSubtitle>{employeeName}</KioskStageSubtitle>
      <KioskStageFormColumn>
        {status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <CircularProgress color="inherit" size={28} />
          </Box>
        )}

        {status === 'error' && <KioskErrorMessage>{t(GENERAL__ERROR_MESSAGE)}</KioskErrorMessage>}

        {status === 'no-planning' && (
          <KioskNoPlanningMessage>{t(KIOSK__START_REGISTRATION_NO_ELIGIBLE_PLANNING)}</KioskNoPlanningMessage>
        )}

        {status === 'ready' && (
          <KioskFieldContainer>
            {planningItems.length > 0 && (
              <Controller
                name="contractDateId"
                control={control}
                render={({ field }) => (
                  <UPDropdown
                    value={field.value}
                    onChange={event => handlePlanningChange(String(event.target.value))}
                    items={planningItems}
                    placeholder={t(GENERAL__SELECT)}
                    label={t(KIOSK__START_REGISTRATION_PLANNING_LABEL)}
                    disabled={Boolean(lockedContractDateId) || submitting}
                    id="kiosk-start-work-planning"
                  />
                )}
              />
            )}

            <KioskFieldRow>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <UPDatepicker
                    value={field.value}
                    onChange={field.onChange}
                    label={t(KIOSK__START_REGISTRATION_DATE_LABEL)}
                    disabled={submitting}
                    minDate={minDate}
                    maxDate={maxDate}
                    disableInput
                    className="datepicker--white-background"
                  />
                )}
              />
              {showCostCentre && (
                <Controller
                  name="costCentreId"
                  control={control}
                  render={({ field }) => (
                    <UPDropdown
                      value={field.value}
                      onChange={event => field.onChange(String(event.target.value))}
                      items={costCentreItems}
                      placeholder={t(GENERAL__SELECT)}
                      label={t(KIOSK__COST_CENTRE_LABEL)}
                      isFilter
                      disabled={submitting}
                      id="kiosk-start-work-cost-centre"
                    />
                  )}
                />
              )}
            </KioskFieldRow>

            <KioskFieldRow>
              <Controller
                name="startWork"
                control={control}
                render={({ field }) => (
                  <UPInput
                    value={field.value}
                    onChange={e => field.onChange(formatKioskTimeInput(field.value, e.target.value))}
                    label={t(KIOSK__START_REGISTRATION_START_LABEL)}
                    placeholder="HH:mm"
                    maxLength={5}
                    inputClassName="white-background"
                    id="kiosk-start-work-start"
                  />
                )}
              />
              {endWorkLabel && (
                <UPInput
                  value={endWorkLabel}
                  onChange={() => undefined}
                  label={t(KIOSK__START_REGISTRATION_END_LABEL)}
                  placeholder=""
                  disabled
                  id="kiosk-start-work-end"
                />
              )}
            </KioskFieldRow>

            {startBeforePlannedStart && (
              <KioskErrorMessage data-testid="kiosk-start-before-planned-start-error">
                {t(KIOSK__START_REGISTRATION_BEFORE_PLANNED_START)}
              </KioskErrorMessage>
            )}

            {policies.length > 0 && (
              <KioskPoliciesAccordion expanded={polOpen} onChange={() => setPolOpen(v => !v)}>
                <KioskPoliciesSummary expandIcon={<ExpandMoreIcon />}>
                  {t(KIOSK__START_REGISTRATION_POLICIES_TITLE)}
                </KioskPoliciesSummary>
                <KioskPoliciesDetails>
                  <KioskPoliciesIntro>{t(KIOSK__START_REGISTRATION_POLICIES_INTRO)}</KioskPoliciesIntro>
                  {policies.map(p => (
                    <KioskPolicyText key={p.key}>{p.label}</KioskPolicyText>
                  ))}
                </KioskPoliciesDetails>
              </KioskPoliciesAccordion>
            )}

            {submitError && <KioskErrorMessage>{t(submitError)}</KioskErrorMessage>}
          </KioskFieldContainer>
        )}

        {status === 'ready' && (
          <KioskStageActions>
            <UPButton
              text={t(KIOSK__START_REGISTRATION_BUTTON)}
              onClick={handleSubmit(onSubmit)}
              disabled={!canSubmit}
              id="kiosk-start-work-submit"
            />
          </KioskStageActions>
        )}
      </KioskStageFormColumn>
    </>
  );
};

export default KioskStartRegistrationView;
